import { createApi } from '@reduxjs/toolkit/query/react';

import {
  AssetCreateDto,
  AssetPropertiesViewDto,
  AssetPropertyListDto,
  AssetPropertyRuleCountDto,
  AssetUpdateDto,
  AssetViewDto,
  SaveAdvancedAssetPropertyDto,
  SearchAssetPropertyByAssetIdsConfig,
} from '../common/dto/asset.dto';
import { DeleteParamsDto, DeleteStatusDto, PaginatedResponse } from '../common/dto/common.dto';
import { FilterDto } from '../common/dto/filter.dto';
import customFetchBase from './base.service';

const API_URI = import.meta.env.VITE_DATA_COLLECTION_SERVICE;
const TAG_TYPE = 'Asset';

export const assetApi = createApi({
  reducerPath: 'assetApi',
  baseQuery: customFetchBase(`${API_URI}/api/asset`),
  tagTypes: [TAG_TYPE],
  endpoints: (builder) => ({
    searchAsset: builder.mutation<PaginatedResponse<AssetViewDto>, Partial<FilterDto>>({
      query(body) {
        return {
          url: ``,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: [TAG_TYPE],
    }),
    filterAsset: builder.mutation<PaginatedResponse<AssetViewDto>, Partial<FilterDto>>({
      query(body) {
        return {
          url: 'filter',
          method: 'PATCH',
          body,
        };
      },
    }),
    getAsset: builder.query<AssetViewDto, string>({
      query: (id: string) => {
        return `/${id}`;
      },
      providesTags: [TAG_TYPE],
    }),
    updateAsset: builder.mutation<AssetViewDto, Partial<AssetUpdateDto>>({
      query(data) {
        return {
          url: ``,
          method: 'PUT',
          body: data,
        };
      },
      invalidatesTags: [TAG_TYPE],
    }),
    addAsset: builder.mutation<AssetViewDto, Partial<AssetCreateDto>>({
      query(body) {
        return {
          url: ``,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: [TAG_TYPE],
    }),
    saveAdvancedProperties: builder.mutation<AssetPropertiesViewDto[], { assetId: string; body: SaveAdvancedAssetPropertyDto[] }>({
      query({ assetId, body }) {
        return {
          url: `${assetId}/properties`,
          method: 'PATCH',
          body,
        };
      },
    }),
    searchAssetPropertyByAssetIds: builder.mutation<AssetPropertyListDto[], Partial<SearchAssetPropertyByAssetIdsConfig>>({
      query(body) {
        return {
          url: `property/search-by-asset-ids`,
          method: 'PATCH',
          body: {
            assetIds: body.assetIds,
            fieldType: body.fieldType,
            config: body.config,
            search: body.search,
          },
        };
      },
      invalidatesTags: [TAG_TYPE],
    }),
    getAssetPropertyRuleCount: builder.query<AssetPropertyRuleCountDto[], string>({
      query: (assetId) => ({
        url: `${assetId}/asset-properties-rules-count`,
        method: 'GET',
      }),
      providesTags: [TAG_TYPE],
    }),
    deleteAsset: builder.mutation<DeleteStatusDto, DeleteParamsDto>({
      query(data: DeleteParamsDto) {
        return {
          url: data.id,
          method: 'DELETE',
          body: data.params ?? {},
        };
      },
      invalidatesTags: [TAG_TYPE],
    }),
    assetDropdownList: builder.mutation<PaginatedResponse<AssetViewDto>, void>({
      query() {
        return {
          url: 'list-for-dropdown',
          method: 'PATCH',
        };
      },
      invalidatesTags: [TAG_TYPE],
    }),
  }),
});

// auto-generated based on the defined endpoints
export const {
  useSearchAssetMutation,
  useFilterAssetMutation,
  useLazyGetAssetQuery,
  useUpdateAssetMutation,
  useAddAssetMutation,
  useDeleteAssetMutation,
  useAssetDropdownListMutation,
  useSaveAdvancedPropertiesMutation,
  useSearchAssetPropertyByAssetIdsMutation,
  useLazyGetAssetPropertyRuleCountQuery,
} = assetApi;
